import { useCallback, type ReactNode } from "react";
import { FILE_META } from "@/components/ui/marks";
import { Panel } from "@/components/ui/Panel";
import {
  useDropzone,
  type FileRejection,
  type FileWithPath,
} from "react-dropzone";
import { ArrowUp, Check, Trash2 } from "lucide-react";
import { Button, IconButton } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  type FileRead,
  ReadStatusChip,
  needsAttention,
} from "@/components/patterns/ReadStatusChip";
import { cn } from "@/lib/utils";
import { toneClasses } from "@/components/ui/tone";

/** A file already held by this component. Shape kept minimal so the intake
 *  page can pass either a server record or an optimistic one. */
export interface UploadedFile {
  id: string;
  name: string;
  /** Optional right-aligned detail — a size, a page count, a status. */
  meta?: string;
  /** The kind of file the attorney said this is, where the slot asked. */
  tag?: string;
  /** What the attorney said this file is, where the slot asked for it. */
  description?: string | null;
  /** The server's own answer on whether this file will accept being re-driven. */
  canRetry?: boolean;
  /** Whether Praxis read the file and how well, once the server has said. */
  read?: FileRead;
  /** The card's element id, for a caller that needs to land on it. */
  elementId?: string;
}

export interface UploadComponentProps {
  /** The slot's element id, for a caller that needs to land on it. */
  id?: string;
  /** Stable identity. Drives `data-testid` and the input's accessible name. */
  name: string;
  title: string;
  /** One line, written for a patent professional, on what belongs here. */
  description: string;
  /** Format labels shown before a file is chosen, e.g. ["DOCX", "PDF"]. */
  acceptedLabels: readonly string[];
  /** MIME type → extensions, as react-dropzone expects. The real gate. */
  accept: Record<string, string[]>;
  files: readonly UploadedFile[];
  onFilesSelected: (files: File[]) => void;
  onRemove: (fileId: string) => void;
  /** Marks the component as gating the advance. Presentation only. */
  required?: boolean;
  /** Position in the set, shown as a leading badge. Omit to drop the badge. */
  ordinal?: number;
  /** Defaults to true — every intake component takes a set. */
  multiple?: boolean;
  /** How many files this component may hold at once. Omit for no limit. */
  maxFiles?: number;
  maxSizeBytes?: number;
  /** An upload is in flight; the control is held but the slot stays readable. */
  busy?: boolean;
  disabled?: boolean;
  /** A message from the caller — a server refusal, say. Shown under the control. */
  error?: string | null;
  /** Called when the picker or a drop rejects a file, after the message is set. */
  onRejected?: (rejections: FileRejection[]) => void;
  /** Extra UI per file — the per-file description supporting material needs. */
  renderFileExtra?: (file: UploadedFile) => ReactNode;
  /**
   * Slot under the held files, for a panel that composes what is about to be
   * added. Named `describe` because `description` is already this
   * component's blurb copy.
   */
  describe?: ReactNode;
  /** The slot is asking about a chosen file, so its act steps aside until it is answered. */
  asking?: boolean;
}

function formatList(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The slot's number, or a tick once it holds a file. A filled slot no longer
 * needs its place in the order said; it needs its state said.
 */
function SlotMark({ ordinal, filled }: { ordinal: number; filled: boolean }) {
  return filled ? (
    <IconTile tone="success" size="xs">
      <Check />
    </IconTile>
  ) : (
    <IconTile size="xs" numeral={ordinal} />
  );
}

function SlotHeading({
  name,
  title,
  required,
  filled,
  ordinal,
}: {
  name: string;
  title: string;
  required: boolean;
  filled: boolean;
  ordinal?: number;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-2.5">
      {ordinal !== undefined && <SlotMark ordinal={ordinal} filled={filled} />}
      <h3 className="flex-1 text-[15px] font-bold tracking-tight">{title}</h3>
      {/* Named, because a held file's row can carry words that read like a
          pill, and asserting on the word alone would find whichever came first. */}
      <StatusPill
        data-testid={`slot-chip-${name}`}
        tone={required ? "destructive" : "neutral"}
        size="sm"
      >
        {required ? "Required" : "Optional"}
      </StatusPill>
    </div>
  );
}

function AcceptedTypes({ labels }: { labels: readonly string[] }) {
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-[10.5px] font-medium text-muted-foreground">
      Accepts
      {labels.map((label) => (
        <span
          key={label}
          className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[9.5px] font-semibold"
        >
          {label}
        </span>
      ))}
    </p>
  );
}

function HeldFiles({
  name,
  files,
  disabled,
  onRemove,
  renderFileExtra,
}: {
  name: string;
  files: readonly UploadedFile[];
  disabled: boolean;
  onRemove: (fileId: string) => void;
  renderFileExtra?: (file: UploadedFile) => ReactNode;
}) {
  return (
    <ul className="mt-2.5">
      {files.map((file) => {
        const flagged =
          file.read !== undefined && needsAttention(file.read.status);
        return (
          <Panel
            as="li"
            shape="row"
            pad="tight"
            key={file.id}
            id={file.elementId}
            // Focusable by script only, so a jump to the card carries focus too.
            tabIndex={-1}
            data-testid={`upload-${name}-file`}
            data-attention={flagged}
            edge={flagged ? "warning" : undefined}
            className="mb-1.5 bg-muted"
          >
            <div className="flex items-center gap-2.5">
              <span className="truncate text-detail font-semibold">
                {file.name}
              </span>
              {file.tag !== undefined && (
                <StatusPill tone="brand" size="sm" plain>
                  {file.tag}
                </StatusPill>
              )}
              <span className="flex-1" />
              {file.meta && <span className={FILE_META}>{file.meta}</span>}
              {file.read && <ReadStatusChip read={file.read} />}
              <IconButton
                variant="quiet"
                size="dense"
                onClick={() => onRemove(file.id)}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
              >
                <Trash2 />
              </IconButton>
            </div>
            {renderFileExtra?.(file)}
          </Panel>
        );
      })}
    </ul>
  );
}

/** What the slot is for, what it takes and what it holds, beside its act. */
function SlotContents({
  name,
  title,
  description,
  acceptedLabels,
  files,
  disabled = false,
  onRemove,
  renderFileExtra,
  describe,
  maxFiles,
  atCapacity,
}: UploadComponentProps & { atCapacity: boolean }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="mb-2.5 text-detail leading-relaxed text-muted-foreground">
        {description}
      </p>
      <AcceptedTypes labels={acceptedLabels} />
      {files.length > 0 && (
        <HeldFiles
          name={name}
          files={files}
          disabled={disabled}
          onRemove={onRemove}
          renderFileExtra={renderFileExtra}
        />
      )}
      {describe}
      {atCapacity && <CapacityNotice title={title} maxFiles={maxFiles} />}
    </div>
  );
}

/** The card's one act, large at its foot, as the design draws it. */
function UploadControl({
  onOpen,
  locked,
  busy,
  filled,
}: {
  onOpen: () => void;
  locked: boolean;
  busy: boolean;
  /** Files already held, so the button offers to add more rather than repeat. */
  filled: boolean;
}) {
  return (
    <Button type="button" size="card" onClick={onOpen} disabled={locked}>
      <ArrowUp aria-hidden />
      {busy ? "Uploading…" : filled ? "Upload More" : "Upload"}
    </Button>
  );
}

/** What to do instead, once the component will take nothing more. */
function CapacityNotice({
  title,
  maxFiles,
}: {
  title: string;
  maxFiles?: number;
}) {
  return (
    <p className="text-[11.5px] text-muted-foreground">
      {maxFiles === 1
        ? "Remove the file above to upload a different one."
        : `This is the most ${title.toLowerCase()} can hold. Remove a file to add another.`}
    </p>
  );
}

function SlotError({ name, message }: { name: string; message: string }) {
  return (
    <Panel
      as="p"
      shape="row"
      pad="tight"
      tone="destructive"
      role="alert"
      data-testid={`upload-${name}-error`}
      className="mt-2.5 text-[11.5px]"
    >
      {message}
    </Panel>
  );
}

/** The dropzone wiring, kept out of the component body. */
function useSlotDropzone({
  accept,
  maxSize,
  disabled,
  multiple,
  room,
  onFilesSelected,
  onRejected,
}: {
  accept: Record<string, string[]>;
  maxSize?: number;
  disabled: boolean;
  multiple: boolean;
  /** Files this component can still take, or undefined for no limit. */
  room?: number;
  onFilesSelected: (files: File[]) => void;
  onRejected?: (rejections: FileRejection[]) => void;
}) {
  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      // The library trims an oversized drop to the room left and rejects only
      // the excess. The slot's rule is stricter: a drop either fits entirely
      // or is refused entirely, so the trimmed files are folded back into the
      // refusal rather than quietly kept.
      const overLimit = rejections.some((rejection) =>
        rejection.errors.some((error) => error.code === "too-many-files"),
      );
      if (overLimit) {
        onRejected?.([
          ...accepted.map((file) => ({
            // The dropzone reads drops through file-selector, so at runtime
            // every accepted file already carries its path fields.
            file: file as FileWithPath,
            errors: [
              {
                code: "too-many-files",
                message: "The drop holds more files than the slot can take",
              },
            ],
          })),
          ...rejections,
        ]);
        return;
      }
      if (rejections.length > 0) onRejected?.(rejections);
      if (accepted.length > 0) onFilesSelected(accepted);
    },
    [onFilesSelected, onRejected],
  );

  return useDropzone({
    onDrop,
    accept,
    maxSize,
    disabled,
    multiple,
    // react-dropzone counts one drop at a time, so this is the room left rather
    // than the component's limit.
    maxFiles: room,
    // The whole slot is the drop target, but only the button opens the picker —
    // otherwise a click meant for a file's remove button opens a dialog too.
    noClick: true,
    noKeyboard: true,
  });
}

/**
 * The card's own state, told through its border.
 *
 * A required slot is drawn solid in the line colour and an optional one
 * dashed, so what the draft cannot start without stands out before anything
 * is added. A slot that holds a file is solid green, whichever it was.
 */
function slotClassName({
  filled,
  required,
  isDragActive,
  disabled,
}: {
  filled: boolean;
  required: boolean;
  isDragActive: boolean;
  disabled: boolean;
}) {
  return cn(
    // `relative` is load-bearing: react-dropzone's input is a 1x1 absolutely
    // positioned element, and without a positioned ancestor it resolves
    // against the page rather than this card. It then keeps document
    // coordinates while the content column scrolls, and stretches the document
    // to wherever the lowest slot's input sits.
    "relative mb-3 border-[1.5px] transition-[border-color,box-shadow]",
    filled
      ? cn(
          "border-solid shadow-[0_3px_12px_-4px_rgba(22,163,74,0.10)]",
          toneClasses("success", "line"),
        )
      : required
        ? "border-solid border-border shadow-[0_1px_2px_rgba(131,83,253,0.06),0_4px_16px_-4px_rgba(131,83,253,0.13)]"
        : "border-dashed border-border",
    isDragActive && "border-solid border-primary bg-primary/[0.03]",
    disabled && "opacity-60",
  );
}

/**
 * What the slot will accept right now, and the dropzone wired to match.
 *
 * The capacity and lock rules decide both what the dropzone is configured with
 * and what the markup draws, so they are settled once, here, rather than read
 * twice from the props.
 */
function useSlotState({
  accept,
  files,
  maxFiles,
  maxSizeBytes,
  multiple = true,
  disabled = false,
  onFilesSelected,
  onRejected,
}: UploadComponentProps) {
  // A component at its limit takes nothing more, by drop or by picker. The
  // attorney removes a file to make room, which also makes replacing one a
  // deliberate act rather than a silent overwrite.
  const atCapacity = maxFiles !== undefined && files.length >= maxFiles;
  // An upload in flight labels the control but does not take it away. Waiting
  // for the round trip before the next file can be chosen makes adding a set of
  // references feel broken, and there is nothing unsafe about a second upload.
  const isLocked = disabled || atCapacity;
  const takesSeveral = multiple && maxFiles !== 1;

  const { getRootProps, getInputProps, open, isDragActive } = useSlotDropzone({
    accept,
    maxSize: maxSizeBytes,
    disabled: isLocked,
    multiple: takesSeveral,
    room: maxFiles === undefined ? undefined : maxFiles - files.length,
    onFilesSelected,
    onRejected,
  });

  return {
    atCapacity,
    isLocked,
    filled: files.length > 0,
    isDragActive,
    getRootProps,
    getInputProps,
    open,
  };
}

/**
 * One intake component: a titled drop target that also opens the file picker.
 *
 * Presentational and controlled — it holds no file state and performs no
 * upload. The caller decides what a selection means, which is what lets the
 * same component serve every slot the API serves, whose rules differ.
 *
 * Accepted formats are declared twice on purpose: `acceptedLabels` is what the
 * attorney reads, `accept` is what actually gates. They are separate because
 * the labels say "DOCX" where the gate needs a MIME type and an extension, and
 * because a component may accept an alias it should not advertise.
 */
export function UploadComponent(props: UploadComponentProps) {
  const {
    id,
    name,
    title,
    required = false,
    ordinal,
    busy = false,
    disabled = false,
    error = null,
  } = props;
  const {
    atCapacity,
    isLocked,
    filled,
    isDragActive,
    getRootProps,
    getInputProps,
    open,
  } = useSlotState(props);

  return (
    <Panel
      as="section"
      {...getRootProps()}
      id={id}
      tabIndex={-1}
      data-testid={`upload-${name}`}
      data-filled={filled ? "true" : "false"}
      data-drag-active={isDragActive ? "true" : "false"}
      data-at-capacity={atCapacity ? "true" : "false"}
      className={slotClassName({ filled, required, isDragActive, disabled })}
    >
      <input {...getInputProps()} aria-label={`Upload ${title}`} />

      <SlotHeading
        name={name}
        title={title}
        required={required}
        filled={filled}
        ordinal={ordinal}
      />
      {/* What the slot holds sits beside its act, which keeps to the card's
          foot however many files the slot holds. */}
      <div
        className={cn("flex items-end gap-4", ordinal !== undefined && "ml-9")}
      >
        <SlotContents {...props} atCapacity={atCapacity} />
        {!atCapacity && !props.asking && (
          <UploadControl
            onOpen={open}
            locked={isLocked}
            busy={busy}
            filled={filled}
          />
        )}
      </div>

      {error && <SlotError name={name} message={error} />}
    </Panel>
  );
}

/**
 * The refusal wording the design asks for.
 *
 * Takes the rejections rather than assuming the reason: a drop refused for
 * being too many files is not a file-type problem, and telling the attorney
 * which formats are accepted would send them looking for a fault that is not
 * there.
 */
export function describeRejection(
  title: string,
  acceptedLabels: readonly string[],
  rejections: readonly FileRejection[] = [],
): string {
  const tooMany = rejections.some((r) =>
    r.errors.some((e) => e.code === "too-many-files"),
  );
  if (tooMany) {
    return `That is more files than ${title.toLowerCase()} can hold. Remove one first, or choose fewer.`;
  }
  return `That file type is not accepted for ${title.toLowerCase()}. Accepted types are ${formatList(acceptedLabels)}.`;
}
